import { useCurrency } from "@/lib/currency";
import { priceUnitShort } from "@/lib/catalog-presentation";
// "También te puede gustar": hasta tres tours reales del mismo destino o con
// experiencia relacionada. Sin calificaciones, descuentos ni etiquetas
// inventadas; todo proviene del catálogo unificado.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";

import { listUnifiedCatalogLite } from "@/lib/catalog.functions";
import { trackTourV2 } from "@/lib/tour-v2-analytics";
import { formatDuration } from "@/lib/slug";
import { useFormatPrice } from "@/hooks/use-format-price";

export function TourV2Related({
  slug,
  location,
  categories,
  lang,
}: {
  slug: string;
  location: string | null;
  categories: string[];
  lang: string;
}) {
  useCurrency(); // subscribe: formatPrice reads the currency store
  const { t, i18n } = useTranslation();
  const formatPrice = useFormatPrice();
  const catalog = useQuery({
    queryKey: ["catalog-lite", lang],
    staleTime: 10 * 60_000,
    queryFn: () => listUnifiedCatalogLite({ data: { lang } }),
  });

  const related = useMemo(() => {
    const all = (catalog.data?.tours ?? []).filter((x) => x.slug !== slug && x.available !== false);
    const place = (location ?? "").toLowerCase().trim();
    const cats = new Set(categories.map((c) => c.toLowerCase()));
    const score = (x: (typeof all)[number]) => {
      let s = 0;
      const loc = (x.location ?? "").toLowerCase();
      if (place && loc && (loc.includes(place) || place.includes(loc))) s += 3;
      if (x.categories?.some((c) => cats.has(c.toLowerCase()))) s += 2;
      if (x.priceFrom != null) s += 1;
      return s;
    };
    return all
      .map((x) => ({ x, s: score(x) }))
      .filter((r) => r.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 3)
      .map((r) => r.x);
  }, [catalog.data, slug, location, categories]);

  if (related.length === 0) return null;

  return (
    <section className="mt-10">
      <h2 className="tour-display text-xl">
        {t("tourV2.related", { defaultValue: "También te puede gustar" })}
      </h2>
      <div className="-mx-4 mt-4 flex snap-x gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0">
        {related.map((r) => (
          <article
            key={r.slug}
            className="w-[78vw] shrink-0 snap-start overflow-hidden rounded-3xl border border-border/70 bg-card sm:w-auto"
          >
            {r.images[0] && (
              <img
                src={r.images[0]}
                alt={r.title}
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full object-cover"
              />
            )}
            <div className="p-4">
              <p className="tour-display line-clamp-2 text-base">{r.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {[r.location, formatDuration(r.durationMinutes)].filter(Boolean).join(" · ")}
              </p>
              {r.priceFrom != null && (
                <p className="mt-1 text-sm font-bold">
                  {formatPrice(r.priceFrom, r.currency ?? "MXN")}{" "}
                  <span className="font-normal text-muted-foreground">{priceUnitShort(r as never, i18n.language)}</span>
                </p>
              )}
              <Link
                to="/tour/$slug"
                params={{ slug: r.slug }}
                onClick={() => trackTourV2("related_tour_clicked", { tourSlug: r.slug })}
                className="mt-3 inline-flex h-10 w-full items-center justify-center rounded-full border border-border text-sm font-semibold transition hover:bg-accent"
              >
                {t("tourV2.seeExperience", { defaultValue: "Ver experiencia" })}
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
